import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Download, FileBarChart2 } from "lucide-react";
import { listInvoices, listPayments, getReceivablesAging, getJobProfitability, listExpenses, getDashboardMetrics } from "@/lib/accounting/accounting.functions";
import { downloadCSV, toCSV } from "@/lib/accounting/exports";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/accounting/reports")({
  component: Reports,
});

function Reports() {
  const invFn = useServerFn(listInvoices);
  const payFn = useServerFn(listPayments);
  const arFn = useServerFn(getReceivablesAging);
  const profFn = useServerFn(getJobProfitability);
  const expFn = useServerFn(listExpenses);
  const dashFn = useServerFn(getDashboardMetrics);

  const run = async (name: string, build: () => Promise<{ rows: any[] }>) => {
    try {
      const { rows } = await build();
      if (!rows.length) { toast.message("No data for this report."); return; }
      downloadCSV(`${name}_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(rows));
      toast.success(`${name} exported`);
    } catch (e) { toast.error((e as Error).message); }
  };

  const reports = [
    {
      name: "invoice_report", title: "Invoice report",
      build: async () => {
        const data = await invFn();
        return { rows: data.map((r: any) => ({ invoice_number: r.invoice_number, customer: r.customer_name, address: r.property_address, date: r.issue_date, due_date: r.due_date, status: r.status, subtotal: r.subtotal, tax: r.tax_amount, total: r.total, paid: r.amount_paid, balance_due: r.balance_due })) };
      },
    },
    {
      name: "payment_report", title: "Payment report",
      build: async () => {
        const data = await payFn();
        return { rows: data.map((p: any) => ({ date: p.recorded_at, customer: p.invoices?.customer_name ?? p.customer_projects?.customer_name, job: p.customer_projects?.project_name, invoice: p.invoices?.invoice_number, method: p.method, amount: p.amount, reference: p.reference })) };
      },
    },
    {
      name: "ar_aging", title: "Accounts receivable aging",
      build: async () => {
        const data = await arFn();
        return { rows: data.rows.map((r: any) => ({ customer: r.customer_name, invoice: r.invoice_number, due_date: r.due_date, days_overdue: r.days_overdue, amount_due: r.balance_due })) };
      },
    },
    {
      name: "revenue_summary", title: "Revenue summary",
      build: async () => {
        const data = await dashFn({ data: {} });
        return { rows: data.revenueByMonth.map((m: any) => ({ month: m.month, revenue: m.amount })) };
      },
    },
    {
      name: "job_profitability", title: "Job profitability",
      build: async () => {
        const data = await profFn();
        return { rows: data.map((r: any) => ({ job: r.project_name, customer: r.customer_name, revenue: r.payments_collected, costs: r.total_costs, gross_profit: r.gross_profit, margin_pct: r.profit_margin.toFixed(1) })) };
      },
    },
    {
      name: "sales_tax", title: "Sales tax report",
      build: async () => {
        const data = await invFn();
        const active = data.filter((i: any) => i.status !== "void");
        const tax = active.reduce((s: number, i: any) => s + Number(i.tax_amount || 0), 0);
        const taxable = active.reduce((s: number, i: any) => s + Number(i.taxable_subtotal || 0), 0);
        const nontax = active.reduce((s: number, i: any) => s + (Number(i.subtotal || 0) - Number(i.taxable_subtotal || 0)), 0);
        return { rows: [{ taxable_sales: taxable, non_taxable_sales: nontax, tax_collected: tax, period: "all_time" }] };
      },
    },
    {
      name: "expense_report", title: "Expense report",
      build: async () => {
        const data = await expFn();
        return { rows: data.map((e: any) => ({ date: e.expense_date, vendor: e.vendor, category: e.category, job: e.customer_projects?.project_name, amount: e.amount, notes: e.notes })) };
      },
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {reports.map((r) => (
        <button key={r.name} onClick={() => run(r.name, r.build)} className="text-left p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-white"><FileBarChart2 className="size-4 text-amber-300" /><span className="font-medium">{r.title}</span></div>
            <p className="text-xs text-zinc-500 mt-1">Click to download CSV</p>
          </div>
          <Download className="size-4 text-zinc-500" />
        </button>
      ))}
    </div>
  );
}